import { useEffect, useMemo, useRef, useState } from "react";
import type StandardRow from "shared-ui/src/types/ecommerce/StandardRow";
import { ProductDetailDrawerRequestDto } from "@lib/ShiOneClient";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { BUNDLE_PRODUCT_CLASS } from "shared-ui/src/types/ecommerce/constants";
import { ProductDetailDrawer } from "shared-ui/src/components/ecommerce/product-detail-drawer/ProductDetailDrawer";
import {
  ProductDrawerTab,
  type DrawerImage,
  type ProductDrawerViewModel,
} from "shared-ui/src/components/ecommerce/product-detail-drawer/types";
import useStandardsProductDetailDrawer from "@features/ecommerce/hooks/useStandardsProductDetailDrawer";
import useStandardsProductAvailability from "@features/ecommerce/hooks/useStandardsProductAvailability";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import { useOrgUnitIdCurrent } from "../../../../../../auth/useOrgUnitIdCurrent";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import { getAddToCartDisabledReason } from "shared-ui/src/components/ecommerce/product-detail-drawer/addToCartDisabledReason";

export const StandardsDrawerTab = ProductDrawerTab;
export type StandardsDrawerTab = ProductDrawerTab;

export interface StandardsShopProductDetailDrawerProps {
  open: boolean;
  product: StandardRow | null;
  initialTab?: StandardsDrawerTab;
  onClose: () => void;
  onAddToCart: (quantity: number) => void;
  isAddingToCart?: boolean;
  /** Fired when the user switches tabs, so a routed caller can write the tab to the URL. */
  onTabChange?: (tab: StandardsDrawerTab) => void;
  /** Called once per product when a settled detail lookup fails or finds no product; routed hosts toast and close. */
  onDetailUnavailable?: () => void;
}

export function StandardsShopProductDetailDrawer({
  open,
  product,
  initialTab = ProductDrawerTab.ProductDetails,
  onClose,
  onAddToCart,
  isAddingToCart,
  onTabChange,
  onDetailUnavailable,
}: StandardsShopProductDetailDrawerProps) {
  const { formatAmount } = useStorefrontCurrency();
  const [stockActivated, setStockActivated] = useState(
    initialTab === ProductDrawerTab.StockInventory,
  );

  useEffect(() => {
    setStockActivated(initialTab === ProductDrawerTab.StockInventory);
  }, [product?.stringId, initialTab]);

  const request = useMemo<ProductDetailDrawerRequestDto | null>(() => {
    if (!open || !product?.productDescriptor || !product.productClass) {
      return null;
    }
    return new ProductDetailDrawerRequestDto({
      productDescriptor: product.productDescriptor.toString(),
      productClass: product.productClass.toLowerCase(),
    });
  }, [open, product?.productDescriptor, product?.productClass]);

  const { data, isLoading, isError } = useStandardsProductDetailDrawer(request);
  const detail = data?.productDetail;

  // isLoading stays true while the query is disabled, so a gated lookup never reports.
  const unavailableKey =
    request != null && !isLoading && (isError || detail == null)
      ? `${request.productClass}~${request.productDescriptor}`
      : null;
  const reportedUnavailableKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !open ||
      !onDetailUnavailable ||
      unavailableKey == null ||
      reportedUnavailableKeyRef.current === unavailableKey
    ) {
      return;
    }
    reportedUnavailableKeyRef.current = unavailableKey;
    onDetailUnavailable();
  }, [open, onDetailUnavailable, unavailableKey]);

  const productName = detail?.productName ?? product?.name ?? "";
  const mfrPartNumber = detail?.mfrPartNum ?? product?.mfgPartNumber ?? "";
  const shiPartNumber = product?.shiPartNumber?.toString() ?? "";
  const price = detail?.price ?? product?.price ?? null;
  const inventoryStatus = detail?.inventoryStatus ?? product?.inventoryStatus;

  const productDescriptor =
    detail?.productDescriptor ?? product?.productDescriptor?.toString() ?? null;
  const isConfigSystem =
    product?.productClass?.toLowerCase() === BUNDLE_PRODUCT_CLASS;
  const productDetailUrl = productDescriptor
    ? `${getShiComBaseUrl()}/product/${isConfigSystem ? "configsystem/" : ""}${productDescriptor}`
    : null;

  const canAddToCart =
    product?.canAddToCart === true &&
    product?.productDescriptor != null &&
    product.productClass != null;
  const { hasPermission } = useClaims();
  const addToCartDisabledReason = getAddToCartDisabledReason({
    isLoading,
    // Matches the add endpoint's ShoppingCartSave gate (EcommerceShoppingCartController "add").
    hasCartPermission: hasPermission(ShiOnePermission.shoppingCartSave),
    hasProductIdentifiers:
      product?.productDescriptor != null && product?.productClass != null,
    canAddToCart: product?.canAddToCart,
  });

  const currentOrgUnitId = useOrgUnitIdCurrent();
  const availabilityEnabled =
    open && stockActivated && productDescriptor != null;
  const {
    data: availabilityData,
    isLoading: isAvailabilityLoading,
    isError: isAvailabilityError,
    refetch: refetchAvailability,
  } = useStandardsProductAvailability(
    productDescriptor,
    currentOrgUnitId,
    availabilityEnabled,
  );

  const images: DrawerImage[] = useMemo(() => {
    const enriched = (data?.images ?? [])
      .filter((image) => image.url)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((image) => ({
        url: image.url as string,
        alt: `${productName} - ${image.angle ?? "view"}`,
      }));
    if (enriched.length > 0) return enriched;
    return product?.imageLocation
      ? [{ url: product.imageLocation, alt: productName }]
      : [];
  }, [data?.images, product?.imageLocation, productName]);

  const viewModel: ProductDrawerViewModel | null = product
    ? {
        productName,
        mfrPartNum: mfrPartNumber || null,
        shiPartNumber: shiPartNumber || null,
        productDescriptor,
        price,
        parsedDescriptionsList:
          detail?.parsedDescriptionsList ?? product.parsedDescriptionList,
        category: detail?.category ?? product.category,
        manufacturer: detail?.manufacturer ?? product.manufacturer,
        inventoryStatus,
        productClass: product.productClass,
        canAddToCart,
      }
    : null;

  return (
    <ProductDetailDrawer
      open={open}
      onClose={onClose}
      product={viewModel}
      images={images}
      fullSpecification={data?.fullSpecification}
      specificationFallback={data?.specification}
      loading={isLoading}
      productDetailUrl={productDetailUrl}
      isConfigSystem={isConfigSystem}
      telemetrySection="standards_quick_view"
      formatPrice={formatAmount}
      initialTab={initialTab}
      onTabChange={(tab) => {
        if (tab === ProductDrawerTab.StockInventory) setStockActivated(true);
        onTabChange?.(tab);
      }}
      cart={{
        canAddToCart: addToCartDisabledReason == null,
        disabledReason: addToCartDisabledReason,
        isAddingToCart,
        onAddToCart,
      }}
      stockTab={{
        data: availabilityData,
        isLoading: isAvailabilityLoading,
        isError: isAvailabilityError,
        onRefresh: refetchAvailability,
      }}
    />
  );
}
